import React from 'react';

import { CopyOutlined } from '@ant-design/icons';
import { Spin, Switch } from 'antd';
import { useSelector } from 'dva';
import { getIntl } from 'umi';
import { isMobileOnly } from 'react-device-detect';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import tableRowButtonsStyles from '@/components/Common/TableRowButtons/style.less';
import { createEnumOptions } from '@/utils/core/data-transformation';
import {
  getEnumTranslationKey,
  normalizeNumericEnumValue,
  normalizeStringEnumValue,
} from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { createTableActionsColumn, formatTZLocal, getEnumNamespace } from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { registerModuleFilters } from '@/utils/input/filters';
import { buildAuditFilters } from '@/utils/input/filters/auditFilters';
import BaseFilters from '@/utils/input/filters/BaseFilters';

import {
  DefinitionDetailStack,
  DefinitionGroupBlock,
  DefinitionRuleLine,
} from './DefinitionDetailValue';
import { MODEL_NAME } from './constants';

const { CompanyAccessPolicyEffect } = CoreEnums;

const EFFECT_BADGE_BY_VALUE = {
  [CompanyAccessPolicyEffect.Allow]: { status: 'success' },
  [CompanyAccessPolicyEffect.Deny]: { status: 'error' },
};

const resolveEffectNumericValue = (value) => {
  if (typeof value === 'string') {
    return value.toLowerCase() === 'allow'
      ? CompanyAccessPolicyEffect.Allow
      : CompanyAccessPolicyEffect.Deny;
  }
  return value;
};

const EffectFilter = {
  ...BaseFilters.EnumFilter,
  name: 'effect',
  label: 'accessPolicy.columns.effect',
  enumNamespace: getEnumNamespace(CoreEnums.CompanyAccessPolicyEffect, 'AccessPolicy'),
  enumSource: CoreEnums.CompanyAccessPolicyEffect,
  options: createEnumOptions(CoreEnums.CompanyAccessPolicyEffect, 'accessPolicy.effect', {
    casing: 'camel',
  }),
  badgeOptions: EFFECT_BADGE_BY_VALUE,
  badgeType: 'group-item',
};

/**
 * CompanyAccessPolicy stores creator/modifier as email strings — filter with
 * `creator eq 'email'` / `modifier eq 'email'` (same as media-categories / email-formats).
 * Do not use buildAuditFilters() userCreatorId/userModifierId here.
 */
const CreatorEmailFilter = {
  ...Filters.UserFilter,
  name: 'creator',
  label: 'config.info.createdBy',
  needsQuotes: true,
  valueField: 'email',
  params: {
    ...Filters.UserFilter.params,
    select: 'id,fullName,email',
  },
};

const ModifierEmailFilter = {
  ...Filters.UserFilter,
  name: 'modifier',
  label: 'config.info.updatedBy',
  needsQuotes: true,
  valueField: 'email',
  params: {
    ...Filters.UserFilter.params,
    select: 'id,fullName,email',
  },
};

const [isActiveFilter, , , createdFilter, updatedFilter] = buildAuditFilters();

export const filtersConfig = [
  { ...isActiveFilter, badgeType: 'group-item' },
  EffectFilter,
  CreatorEmailFilter,
  ModifierEmailFilter,
  createdFilter,
  updatedFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

const formatRuleValuesForDisplay = (values) =>
  Array.isArray(values) ? values.join(', ') : values != null ? String(values) : '';

const resolveRuleTypeTranslationKey = (raw) => {
  const rt = normalizeNumericEnumValue(
    CoreEnums.CompanyAccessPolicyRuleType,
    raw?.value ?? raw,
    CoreEnums.CompanyAccessPolicyRuleType.ScopeRole,
  );
  return getEnumTranslationKey(CoreEnums.CompanyAccessPolicyRuleType, rt, 'accessPolicy.ruleType');
};

const resolveOperatorTranslationKey = (raw) => {
  const op = normalizeStringEnumValue(
    CoreEnums.ComparisonOperator,
    raw?.value ?? raw,
    CoreEnums.ComparisonOperator.Equal,
  );
  return getEnumTranslationKey(CoreEnums.ComparisonOperator, op, 'accessPolicy.operator');
};

const resolveLogicalOperatorTranslationKey = (raw) => {
  const op = normalizeNumericEnumValue(
    CoreEnums.LogicalOperator,
    raw?.value ?? raw,
    CoreEnums.LogicalOperator.And,
  );
  return getEnumTranslationKey(CoreEnums.LogicalOperator, op, 'global.field');
};

const renderRuleLine = (rule, formatMessage, key) => {
  if (!rule || typeof rule !== 'object') return null;
  const fm = formatMessage || ((descriptor, values) => getIntl().formatMessage(descriptor, values));
  return (
    <DefinitionRuleLine
      key={key}
      typeFieldLabel={fm({ id: 'global.type' })}
      typeLabel={fm({ id: resolveRuleTypeTranslationKey(rule.ruleType) })}
      operatorFieldLabel={fm({ id: 'accessPolicy.definition.operator' })}
      operatorLabel={fm({ id: resolveOperatorTranslationKey(rule.operator) })}
      valuesFieldLabel={fm({ id: 'accessPolicy.definition.values' })}
      values={formatRuleValuesForDisplay(rule.values)}
    />
  );
};

const renderGroupBlocks = (groups, formatMessage, depth = 1, keyPrefix = 'g') => {
  const fm = formatMessage || ((descriptor, values) => getIntl().formatMessage(descriptor, values));
  return (Array.isArray(groups) ? groups : [])
    .map((group, idx) => {
      if (!group || typeof group !== 'object') return null;
      const title = fm({ id: 'accessPolicy.definition.groupTitle' }, { index: idx + 1, depth });
      const keyBase = `${keyPrefix}_${depth}_${idx}`;
      const ruleLines = (Array.isArray(group.rules) ? group.rules : [])
        .map((rule, ruleIdx) => renderRuleLine(rule, fm, `${keyBase}_r_${ruleIdx}`))
        .filter(Boolean);
      const nested = renderGroupBlocks(group.groups, fm, depth + 1, `${keyBase}_n`);

      return (
        <DefinitionGroupBlock
          key={keyBase}
          title={title}
          logicalOperatorFieldLabel={fm({
            id: 'accessPolicy.definition.logicalOperator',
          })}
          logicalOperatorLabel={fm({
            id: resolveLogicalOperatorTranslationKey(group.logicalOperator),
          })}
        >
          {ruleLines}
          {nested}
        </DefinitionGroupBlock>
      );
    })
    .filter(Boolean);
};

/** Single "Reglas" section: one line per rule → tipo · operador · valores. */
export function buildAccessPolicyRuleDetailRows(definition, formatMessage) {
  const rules = Array.isArray(definition?.rules) ? definition.rules : [];
  if (rules.length === 0) return [];

  const lines = rules
    .map((rule, idx) => renderRuleLine(rule, formatMessage, `root_rule_${idx}`))
    .filter(Boolean);
  if (lines.length === 0) return [];

  return [
    {
      name: 'definitionRules',
      label: 'accessPolicy.definition.rules',
      value: <DefinitionDetailStack>{lines}</DefinitionDetailStack>,
      type: 'text',
      locale: false,
    },
  ];
}

/** Single "Grupos" section: group header + nested rule lines. */
export function buildAccessPolicyGroupDetailRows(definition, formatMessage) {
  const groups = Array.isArray(definition?.groups) ? definition.groups : [];
  if (groups.length === 0) return [];

  const blocks = renderGroupBlocks(groups, formatMessage);
  if (blocks.length === 0) return [];

  return [
    {
      name: 'definitionGroups',
      label: 'accessPolicy.definition.groups',
      value: <DefinitionDetailStack>{blocks}</DefinitionDetailStack>,
      type: 'text',
      locale: false,
    },
  ];
}

const normalizeDefinitionRuleForForm = (rule) => ({
  ...rule,
  ruleType: normalizeNumericEnumValue(
    CoreEnums.CompanyAccessPolicyRuleType,
    rule?.ruleType?.value ?? rule?.ruleType,
    CoreEnums.CompanyAccessPolicyRuleType.ScopeRole,
  ),
  operator: normalizeStringEnumValue(
    CoreEnums.ComparisonOperator,
    rule?.operator?.value ?? rule?.operator,
    CoreEnums.ComparisonOperator.Equal,
  ),
  values: Array.isArray(rule?.values) ? rule.values.join(', ') : rule?.values ?? '',
});

const normalizeDefinitionGroupsForForm = (groups) =>
  (Array.isArray(groups) ? groups : []).map((group) => ({
    logicalOperator: normalizeNumericEnumValue(
      CoreEnums.LogicalOperator,
      group?.logicalOperator,
      CoreEnums.LogicalOperator.And,
    ),
    rules: (Array.isArray(group?.rules) ? group.rules : []).map(normalizeDefinitionRuleForForm),
    groups: normalizeDefinitionGroupsForForm(group?.groups),
  }));

const normalizeRuleValuesForApi = (values) => {
  if (typeof values === 'string') {
    return values
      ? values
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  }
  return Array.isArray(values) ? values : [];
};

/** Recursive API shape: never emit `groups` when empty (nested or root). */
const normalizeDefinitionGroupsForApi = (groups) =>
  (Array.isArray(groups) ? groups : []).map((group) => {
    const nestedGroups = normalizeDefinitionGroupsForApi(group?.groups);
    const node = {
      logicalOperator: normalizeNumericEnumValue(
        CoreEnums.LogicalOperator,
        group?.logicalOperator?.value ?? group?.logicalOperator,
        CoreEnums.LogicalOperator.And,
      ),
      rules: (Array.isArray(group?.rules) ? group.rules : [])
        .map((row) => {
          if (!row || typeof row !== 'object') return null;
          const ruleType = normalizeNumericEnumValue(
            CoreEnums.CompanyAccessPolicyRuleType,
            row?.ruleType?.value ?? row?.ruleType,
            CoreEnums.CompanyAccessPolicyRuleType.ScopeRole,
          );
          const operator = normalizeStringEnumValue(
            CoreEnums.ComparisonOperator,
            row?.operator?.value ?? row?.operator,
            CoreEnums.ComparisonOperator.Equal,
          );
          if (ruleType == null || ruleType === '' || operator == null || operator === '')
            return null;
          return {
            ruleType,
            operator,
            values: normalizeRuleValuesForApi(row?.values),
          };
        })
        .filter(Boolean),
    };
    if (nestedGroups.length > 0) {
      node.groups = nestedGroups;
    }
    return node;
  });

/** Prefill / normalize definition fields for add/edit. */
export function prepareAccessPolicyFields(fields) {
  return fields.map((f) => {
    if (f.name === 'definitionLogicalOperator') {
      return {
        ...f,
        value: normalizeNumericEnumValue(
          CoreEnums.LogicalOperator,
          f.value,
          CoreEnums.LogicalOperator.And,
        ),
      };
    }
    if (f.name === 'definitionRules') {
      const arr = Array.isArray(f.value) ? f.value : [];
      return { ...f, value: arr.map(normalizeDefinitionRuleForForm) };
    }
    if (f.name === 'definitionGroups') {
      return {
        ...f,
        value: normalizeDefinitionGroupsForForm(f.value),
      };
    }
    return f;
  });
}

/**
 * Details: hide raw definition editors; list-matching Effect badge;
 * Reglas / Grupos as one section each (tipo · operador · valores per line).
 */
export function mapAccessPolicyDetailFields(rows, { item, formatMessage }) {
  if (!item || !rows?.length) return rows;

  const def = item.definition ?? {};
  let formattedDetails = rows
    .filter((field) => field.name !== 'definitionRules' && field.name !== 'definitionGroups')
    .map((field) => {
      if (field.name !== 'effect') return field;
      const numericValue = resolveEffectNumericValue(item.effect ?? field.value);
      return {
        ...field,
        badgeType: 'group-item',
        badgeStatus: numericValue === CompanyAccessPolicyEffect.Allow ? 'success' : 'error',
      };
    });

  const ruleRows = buildAccessPolicyRuleDetailRows(def, formatMessage);
  const groupRows = buildAccessPolicyGroupDetailRows(def, formatMessage);
  const idxLogical = formattedDetails.findIndex((f) => f.name === 'definitionLogicalOperator');
  const definitionRows = [...ruleRows, ...groupRows];

  if (definitionRows.length > 0) {
    if (idxLogical >= 0) {
      formattedDetails = [
        ...formattedDetails.slice(0, idxLogical + 1),
        ...definitionRows,
        ...formattedDetails.slice(idxLogical + 1),
      ];
    } else {
      formattedDetails = [...formattedDetails, ...definitionRows];
    }
  }

  return formattedDetails;
}

/** Build create/update payload with nested `definition` API shape. */
export function buildAccessPolicySubmitData(payload, { isEdit, item, id }) {
  const { definitionLogicalOperator, definitionRules, definitionGroups, ...rest } = payload;

  const rules = (Array.isArray(definitionRules) ? definitionRules : [])
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const ruleType = normalizeNumericEnumValue(
        CoreEnums.CompanyAccessPolicyRuleType,
        row.ruleType?.value ?? row.ruleType,
        CoreEnums.CompanyAccessPolicyRuleType.ScopeRole,
      );
      const operator = normalizeStringEnumValue(
        CoreEnums.ComparisonOperator,
        row.operator?.value ?? row.operator,
        CoreEnums.ComparisonOperator.Equal,
      );
      const values = normalizeRuleValuesForApi(row.values);
      if (ruleType == null || ruleType === '' || operator == null || operator === '') return null;
      return { ruleType, operator, values };
    })
    .filter(Boolean);

  const groupsPayload = normalizeDefinitionGroupsForApi(definitionGroups);
  const definition = {
    logicalOperator: definitionLogicalOperator ?? CoreEnums.LogicalOperator.And,
    rules,
  };
  if (groupsPayload.length > 0) {
    definition.groups = groupsPayload;
  }

  return {
    ...(isEdit ? { ...item, id } : {}),
    ...rest,
    definition,
  };
}

const AccessPolicyRowActions = ({
  record,
  formatMessage,
  openDrawerCreateOrEdit,
  deleteItemConfirm,
  duplicate,
  isMobileView,
}) => {
  const isRowActionLoading = useSelector(
    (state) => !!state[MODEL_NAME]?.rowActionLoadingItemIds?.[record.id],
  );

  if (isRowActionLoading) {
    return (
      <div className={tableRowButtonsStyles.loading}>
        <Spin size="small" />
      </div>
    );
  }

  return (
    <TableRowButtons
      onDetail={() => openDrawerCreateOrEdit(record.id, true, record)}
      onEdit={() => openDrawerCreateOrEdit(record.id, false)}
      onRemove={() => deleteItemConfirm(record.id)}
      formatMessage={formatMessage}
      isMobileView={isMobileView}
      actionOrder={{
        primary: [],
        secondary: ['duplicate', 'divider', 'detail', 'edit', 'delete'],
      }}
      customItems={[
        {
          key: 'duplicate',
          label: formatMessage({ id: 'accessPolicy.actions.duplicate' }),
          icon: <CopyOutlined />,
          onClick: () => duplicate?.(record.id),
        },
        { key: 'divider', type: 'divider' },
      ]}
    />
  );
};

export function buildAccessPolicyColumns({
  formatMessage,
  searchCriteria,
  openDrawerCreateOrEdit,
  deleteItemConfirm,
  activate,
  activatingItemIds,
  duplicate,
  isMobileView = isMobileOnly,
}) {
  return [
    {
      key: 'isActive',
      title: formatMessage({ id: 'global.text.enabled' }),
      dataIndex: 'isActive',
      sorter: true,
      align: 'center',
      width: 120,
      render: (_, record) => (
        <Switch
          checked={record.isActive}
          loading={!!activatingItemIds?.[record.id]}
          onChange={(active) => activate(record.id, active)}
        />
      ),
    },
    {
      key: 'name',
      title: formatMessage({ id: 'global.name' }),
      dataIndex: 'name',
      sorter: true,
      render: createHighlightRender(searchCriteria),
    },
    {
      key: 'effect',
      title: formatMessage({ id: 'accessPolicy.columns.effect' }),
      dataIndex: 'effect',
      sorter: true,
      align: 'center',
      render: (value) => {
        const numericValue = resolveEffectNumericValue(value);
        const translationKey = getEnumTranslationKey(
          CompanyAccessPolicyEffect,
          numericValue,
          'accessPolicy.effect',
        );
        const badge = EFFECT_BADGE_BY_VALUE[numericValue] || { status: 'default' };
        return (
          <Badge
            status={badge.status}
            type="group-item"
            text={formatMessage({ id: translationKey })}
          />
        );
      },
    },
    {
      key: 'priority',
      title: formatMessage({ id: 'accessPolicy.columns.priority' }),
      dataIndex: 'priority',
      sorter: true,
    },
    {
      key: 'updated',
      title: formatMessage({ id: 'global.updated' }),
      dataIndex: 'updated',
      sorter: true,
      render: (value) => (value ? formatTZLocal(value) : null),
    },
    createTableActionsColumn({
      alwaysDropdown: true,
      render: (_, record) => (
        <AccessPolicyRowActions
          record={record}
          formatMessage={formatMessage}
          openDrawerCreateOrEdit={openDrawerCreateOrEdit}
          deleteItemConfirm={deleteItemConfirm}
          duplicate={duplicate}
          isMobileView={isMobileView}
        />
      ),
    }),
  ];
}
